import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useQuery } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  Equipment,
  EquipmentProperty as EquipmentPropertyModel,
  EquipmentStateMachine,
} from "../../odata/appu4dm/AppU4DMModel";
import { qEquipment, QEquipment } from "../../odata/appu4dm/QAppU4DM";
import { machineKeys } from "./machine-keys";

export function useAvailableMachines() {
  const { data, ...query } = useQuery({
    queryKey: machineKeys.listAvailable(),
    queryFn: ({ meta }) => fetchActiveMachines(meta!.token!),
    enabled: ({ meta }) => !!meta?.token,
  });

  return {
    availableMachines: data?.value,
    ...query,
  };
}

type StateMachine = Pick<
  EquipmentStateMachine,
  "Id" | "Equipment_Id" | "Status"
>;

type EquipmentProperty = Pick<
  EquipmentPropertyModel,
  | "Id"
  | "NId"
  | "PropertyValue"
  | "Operational"
  | "PropertyType"
  | "Equipment_Id"
>;

type AvailableMachine = Pick<
  Equipment,
  | "Id"
  | "CreatedOn"
  | "NId"
  | "Name"
  | "Description"
  | "WorkCalendarNId"
  | "EquipmentConfigurationId"
  | "LevelNId"
  | "Status"
> & { StateMachines: StateMachine[]; EquipmentProperties: EquipmentProperty[] };

async function fetchActiveMachines(token: string) {
  const builder = createQueryBuilderV4("Equipment", new QEquipment())
    .expand("StateMachines")
    .expanding("EquipmentProperties", (b) =>
      b.select(
        "Id",
        "NId",
        "PropertyValue",
        "Operational",
        "PropertyType",
        "Equipment_Id"
      )
    )
    .expanding("StateMachines", (b) => b.select("Id", "Equipment_Id", "Status"))
    .filter(
      qEquipment.LevelNId.eq("Unit")
        .or(qEquipment.LevelNId.eq("WorkCenter"))
        .and(
          qEquipment.StateMachines.any((sm) =>
            sm.Status.getEntity(true).StatusNId.ne("Scrapped")
          )
        )
    )
    .select(
      "Id",
      "CreatedOn",
      "NId",
      "Name",
      "Description",
      "WorkCalendarNId",
      "EquipmentConfigurationId",
      "LevelNId",
      "Status",
      "EquipmentProperties",
      "StateMachines"
    );

  return odataFetch<AvailableMachine>({ builder, token });
}
